import { useRef } from 'react'
import { sentryCaptureException } from '../lib/sentry'

/**
 * Wraps Promise.all API calls with error catching and Sentry reporting.
 * Replaces the duplicated `safe()` pattern in dashboard and market screens.
 *
 * Usage:
 *   const { safe, hadError } = useSafeAPI('dashboard')
 *   const [a, b] = await Promise.all([safe(fetchA(), fallbackA), safe(fetchB(), fallbackB)])
 *   if (hadError.current) setLoadError(true)
 */
export function useSafeAPI(screen: string) {
  const hadError = useRef(false)

  const safe = <T,>(promise: Promise<T>, fallback: T): Promise<T> =>
    promise.catch((err: any) => {
      hadError.current = true
      sentryCaptureException(err, { screen })
      return fallback
    })

  const reset = () => { hadError.current = false }

  return { safe, hadError, reset }
}
